import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Button,
  Field,
  Form,
  FormFooter,
  FormRow,
  FormSection,
  Modal,
  SearchSelect,
  Select,
} from '@evogard/ui';
import type {
  Beneficiary,
  BeneficiaryMember,
  Company,
  CreateBeneficiary,
  UpdateBeneficiary,
} from '@evogard/contract';
import {
  BENEFICIARY_ACCOUNT_TYPES,
  BENEFICIARY_REF_TYPES,
  PIX_KEY_TYPES,
  checkPaymentData,
  checkPixKey,
  COMPANY_TYPES,
  COMPANY_TYPES_BY_CATEGORY,
  companyCategoryOf,
  isValidCnpj,
  isValidCpf,
  normalizePhone,
  pixKeyTypeOf,
  type BeneficiaryAccountType,
  type BeneficiaryRefType,
  type PaymentDataField,
  type PixKeyType,
} from '@evogard/domain';
import { api, ok } from '../../lib/api.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { can, useSessionContext } from '../../lib/session.js';
import { BANKS } from './banks.js';
import {
  ACCOUNT_TYPE_LABELS,
  COMPANY_TYPE_LABELS,
  PIX_PLACEHOLDERS,
  PIX_TYPE_LABELS,
  REF_TYPE_LABELS,
  ownerKindLabel,
} from './labels.js';

/** A chave das consultas de beneficiário — quem salva invalida por ela. */
export const BENEFICIARIES_KEY = ['plataforma', 'beneficiarios'] as const;

/**
 * Como a tela chama o dono do ramo `companhia` — "prestador" ou "instituição",
 * os dois grupos de Sinistros › Cadastros (25/09). Antes de escolher o tipo,
 * as duas palavras juntas.
 */
function companyWords(type: Company['type'] | '') {
  if (type === '') {
    return {
      label: 'Prestador ou instituição',
      the: 'o cadastro',
      aNew: 'novo cadastro',
      none: 'Nenhum cadastro',
    };
  }
  return companyCategoryOf(type) === 'instituicoes'
    ? {
        label: 'Instituição',
        the: 'a instituição',
        aNew: 'nova instituição',
        none: 'Nenhuma instituição',
      }
    : {
        label: 'Prestador',
        the: 'o prestador',
        aNew: 'novo prestador',
        none: 'Nenhum prestador',
      };
}

/*
 * A lista inteira do BACEN, montada uma vez. A busca é local: são ~460
 * bancos, e o rótulo leva o código e o nome — dá para achar por qualquer um.
 */
const BANK_OPTIONS = BANKS.map((b) => ({
  value: b.code,
  label: `${b.code} · ${b.name}`,
}));

type OwnerMode = 'existente' | 'novo';

/**
 * A escolha única de "Tipo de beneficiário" (28/09): os três ramos que não são
 * empresa, e os cinco tipos de empresa no lugar de `companhia` — antes eram
 * duas listas, "Tipo de beneficiário" e depois "Tipo". O ramo gravado sai
 * daqui (`refTypeOf`); os valores não colidem, e é isso que deixa um
 * `<select>` só carregar os dois.
 */
type OwnerKind = Exclude<BeneficiaryRefType, 'companhia'> | Company['type'];

const isCompanyType = (kind: OwnerKind | ''): kind is Company['type'] =>
  (COMPANY_TYPES as readonly string[]).includes(kind);

const refTypeOf = (kind: OwnerKind | ''): BeneficiaryRefType | '' =>
  kind === '' ? '' : isCompanyType(kind) ? 'companhia' : kind;

/**
 * Só instituição financeira e órgão público podem ficar sem conta e sem PIX
 * (decidido em 28/09): a Indenização paga o boleto ou a guia deles, que vai
 * anexado em cada despesa — o cadastro não tem o que guardar. O controle é só
 * desta tela: a API sempre aceitou os dados bancários nulos.
 */
const mayOmitBankData = (companyType: Company['type'] | '' | null | undefined) =>
  !!companyType && companyCategoryOf(companyType) === 'instituicoes';
type Errors = Partial<Record<PaymentDataField | OwnerField, string>>;
type OwnerField =
  'refType' | 'owner' | 'memberDocument' | 'name' | 'document' | 'phone' | 'email';

export interface BeneficiaryFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Presente, o modal EDITA os dados bancários deste; ausente, cadastra um novo. */
  beneficiary?: Beneficiary;
  /**
   * Os tipos de dono que quem abriu aceita — o sinistro que só paga oficina
   * passa `['companhia']`. Ausente, os três.
   */
  refTypes?: readonly BeneficiaryRefType[];
  /** Depois de salvar — para a tela que abriu escolher o beneficiário recém-criado. */
  onSaved?: (beneficiary: Beneficiary) => void;
}

/**
 * O cadastro de beneficiário — os dados bancários de quem recebe um pagamento.
 *
 * Mora em `components/`, e não numa pasta de módulo, porque qualquer módulo o
 * abre: o sinistro cadastra a oficina que vai pagar sem sair da tela dele. O
 * lint proíbe um módulo de importar do outro, e é isso que o põe aqui.
 *
 * ## A permissão, campo a campo
 *
 * Tudo sai de `can()` — a mesma função do guard — e esconder é cortesia: quem
 * protege é a API.
 *
 * - cadastrar exige `platform.beneficiaries:criar`, editar exige `:editar`;
 * - o tipo de dono só aparece se a pessoa puder PROCURAR ou CADASTRAR aquele
 *   dono (`claims.companies`, `claims.third_parties`). O associado vem do ERP e
 *   o consultor vem da lista da praça — os dois cobertos pelo próprio
 *   `:criar` (o consultor desde 25/09/2026: antes pedia `sales.teams:ver`, e o
 *   Financeiro não via o tipo);
 * - "Cadastrar novo" só aparece com `claims.companies:criar` ou
 *   `claims.third_parties:criar` — a API recusa sem ela, e o botão que leva a
 *   uma recusa certa é o defeito que a casa persegue.
 *
 * ## A validação é daqui (decidido em 24/09)
 *
 * A API e o banco não conferem os dados bancários. Quem confere é
 * `checkPaymentData` e `checkPixKey`, de `@evogard/domain`, antes do envio — e
 * a chave vai gravada no formato do BACEN que `checkPixKey` devolve.
 *
 * A única saída de `checkPaymentData` é "Sem dados bancários", e só para
 * instituição financeira e órgão público (`mayOmitBankData`, 28/09): conta e
 * PIX vão nulos, e o boleto ou a guia chega anexado em cada despesa.
 */
export function BeneficiaryFormModal({
  open,
  onOpenChange,
  beneficiary,
  refTypes = BENEFICIARY_REF_TYPES,
  onSaved,
}: BeneficiaryFormModalProps) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={beneficiary ? 'Editar conta de pagamento' : 'Nova conta de pagamento'}
      descricao={
        beneficiary
          ? 'Os dados bancários. Quem recebe não muda: para outro dono, cadastre outra conta de pagamento.'
          : 'Quem recebe o pagamento e como pagar. O mesmo dono pode ter mais de uma conta.'
      }
      size="lg"
    >
      {/* Montado só aberto: reabrir começa do zero, e não do rascunho anterior. */}
      {open && (
        <BeneficiaryForm
          beneficiary={beneficiary}
          refTypes={refTypes}
          onDone={(saved) => {
            onSaved?.(saved);
            onOpenChange(false);
          }}
          onCancel={() => onOpenChange(false)}
        />
      )}
    </Modal>
  );
}

function BeneficiaryForm({
  beneficiary,
  refTypes,
  onDone,
  onCancel,
}: {
  beneficiary?: Beneficiary;
  refTypes: readonly BeneficiaryRefType[];
  onDone: (saved: Beneficiary) => void;
  onCancel: () => void;
}) {
  const client = useQueryClient();
  const { data: context } = useSessionContext();
  const editing = beneficiary !== undefined;

  const allowed = editing
    ? can(context, 'platform.beneficiaries', 'editar')
    : can(context, 'platform.beneficiaries', 'criar');

  // @valor
  const reach: Record<BeneficiaryRefType, { search: boolean; create: boolean }> = {
    associado: { search: true, create: false },
    companhia: {
      search: can(context, 'claims.companies', 'ver'),
      create: can(context, 'claims.companies', 'criar'),
    },
    terceiro: {
      search: can(context, 'claims.third_parties', 'ver'),
      create: can(context, 'claims.third_parties', 'criar'),
    },
    /*
     * O consultor é escolhido na lista da praça (`listBeneficiaryConsultants`,
     * sob o próprio `:criar`) e nunca criado aqui: o vínculo dele é cadastro do
     * comercial.
     */
    consultor: { search: true, create: false },
  };
  const offeredTypes = refTypes.filter((t) => reach[t].search || reach[t].create);
  /* Os ramos que não são empresa, soltos; a empresa, pelos dois grupos da tela. */
  const offeredPeople = offeredTypes.filter(
    (t): t is Exclude<BeneficiaryRefType, 'companhia'> => t !== 'companhia',
  );
  const offersCompany = offeredTypes.includes('companhia');
  const offeredKinds: OwnerKind[] = [
    ...offeredPeople,
    ...(offersCompany ? COMPANY_TYPES : []),
  ];

  /* ---- quem recebe ---- */
  const [kind, setKind] = useState<OwnerKind | ''>(
    offeredKinds.length === 1 ? offeredKinds[0]! : '',
  );
  const refType = refTypeOf(kind);
  const companyType: Company['type'] | '' = isCompanyType(kind) ? kind : '';
  const [ownerMode, setOwnerMode] = useState<OwnerMode>('existente');
  const [ownerId, setOwnerId] = useState('');
  const [ownerLabel, setOwnerLabel] = useState('');
  const [member, setMember] = useState<BeneficiaryMember | null>(null);
  const [memberDocument, setMemberDocument] = useState('');
  const [name, setName] = useState('');
  /*
   * O CPF/CNPJ de quem recebe — gravado em `beneficiaries.document`. Na
   * empresa nova, é o MESMO campo do documento dela: um campo só, que vai para
   * os dois lugares. Nos outros casos vem preenchido com o documento do dono,
   * quando a casa o tem (empresa, associado), e o operador digita o do terceiro.
   */
  /* Beneficiário de antes da coluna abre com o documento do dono, se houver. */
  const initialDocument = beneficiary?.document ?? beneficiary?.owner.document ?? '';
  const [documentType, setDocumentType] = useState<'cpf' | 'cnpj'>(
    initialDocument.length === 14 ? 'cnpj' : 'cpf',
  );
  const [document, setDocument] = useState(initialDocument);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  /* ---- como pagar ---- */
  const [bankCode, setBankCode] = useState(beneficiary?.bankCode ?? '');
  const [agency, setAgency] = useState(beneficiary?.agency ?? '');
  const [accountNumber, setAccountNumber] = useState(beneficiary?.accountNumber ?? '');
  const [accountType, setAccountType] = useState<BeneficiaryAccountType | ''>(
    beneficiary?.accountType ?? '',
  );
  const [holder, setHolder] = useState(beneficiary?.accountHolderName ?? '');
  const [pixKeyType, setPixKeyType] = useState<PixKeyType | ''>(
    pixKeyTypeOf(beneficiary?.pixKey) ?? '',
  );
  const [pixKey, setPixKey] = useState(beneficiary?.pixKey ?? '');

  /* ---- sem dados bancários: só instituição ---- */
  const canOmitBankData = mayOmitBankData(
    editing ? beneficiary.owner.companyType : companyType,
  );
  const [withoutBankData, setWithoutBankData] = useState(
    editing &&
      mayOmitBankData(beneficiary.owner.companyType) &&
      !beneficiary.bankCode &&
      !beneficiary.accountNumber &&
      !beneficiary.pixKey,
  );
  const omitBankData = canOmitBankData && withoutBankData;

  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<string | null>(null);

  const canCreateOwner =
    refType === 'companhia' || refType === 'terceiro' ? reach[refType].create : false;
  const canSearchOwner = refType !== '' && reach[refType].search;
  /* Sem poder procurar, o único caminho é cadastrar — e vice-versa. */
  const mode: OwnerMode =
    refType === 'associado'
      ? 'existente'
      : !canSearchOwner
        ? 'novo'
        : !canCreateOwner
          ? 'existente'
          : ownerMode;

  /** Preenche o documento com o do dono, e o tipo pelo tamanho (14 é CNPJ). */
  const fillDocument = (value: string | null, fallback: 'cpf' | 'cnpj' = 'cpf') => {
    setDocument(value ?? '');
    setDocumentType(value ? (value.length === 14 ? 'cnpj' : 'cpf') : fallback);
  };

  const resetOwner = (
    fallback: 'cpf' | 'cnpj' = refType === 'companhia' ? 'cnpj' : 'cpf',
  ) => {
    setOwnerId('');
    setOwnerLabel('');
    setMember(null);
    fillDocument(null, fallback);
    setErrors({});
  };

  /* A empresa nova tem o documento no formulário dela; os outros, numa linha própria. */
  const ownDocumentRow =
    editing || (refType !== '' && !(refType === 'companhia' && mode === 'novo'));

  const save = useMutation({
    mutationFn: async (): Promise<Beneficiary> => {
      const pix = pixKeyType !== '' ? checkPixKey(pixKeyType, pixKey) : null;
      const bank = BANKS.find((b) => b.code === bankCode);
      const documentDigits = document.replace(/\D/g, '') || null;
      /* Sem dados bancários: tudo nulo, mesmo o que foi digitado antes da escolha. */
      const payment = omitBankData
        ? {
            bankCode: null,
            bankName: null,
            agency: null,
            accountNumber: null,
            accountType: null,
            accountHolderName: null,
            pixKey: null,
            document: documentDigits,
          }
        : {
            bankCode: bankCode || null,
            bankName: bank?.name ?? (bankCode ? (beneficiary?.bankName ?? null) : null),
            agency: agency.trim() || null,
            accountNumber: accountNumber.trim() || null,
            accountType: accountType || null,
            accountHolderName: holder.trim() || null,
            pixKey: pix?.valid ? pix.normalized : null,
            document: documentDigits,
          };

      if (beneficiary) {
        const body: UpdateBeneficiary = payment;
        const r = await api.beneficiaries.updateBeneficiary({
          params: { id: beneficiary.id },
          body,
        });
        return ok(r, 200, 'Não foi possível salvar a conta de pagamento.');
      }

      const body: CreateBeneficiary = { owner: ownerInput(), ...payment };
      const r = await api.beneficiaries.createBeneficiary({ body });
      return ok(r, 201, 'Não foi possível cadastrar a conta de pagamento.');
    },
    onMutate: () => setFailure(null),
    onSuccess: (saved) => {
      void client.invalidateQueries({ queryKey: BENEFICIARIES_KEY });
      if (!editing && mode === 'novo') {
        /* a empresa ou o terceiro também nasceu — as listas deles mudaram */
        void client.invalidateQueries({ queryKey: ['sinistros'] });
      }
      onDone(saved);
    },
    onError: (e: Error) => setFailure(e.message),
  });

  function ownerInput(): CreateBeneficiary['owner'] {
    if (refType === 'associado') {
      return {
        mode: 'existente',
        beneficiaryRefType: 'associado',
        beneficiaryRefId: member!.code,
      };
    }
    if (mode === 'existente') {
      return {
        mode: 'existente',
        beneficiaryRefType: refType as 'companhia' | 'terceiro' | 'consultor',
        beneficiaryRefId: ownerId,
      };
    }
    if (refType === 'companhia') {
      return {
        mode: 'nova_companhia',
        company: {
          type: companyType as Company['type'],
          name: name.trim(),
          documentType,
          document,
          email: email.trim() || null,
          phone: phone.trim() || null,
        },
      };
    }
    return {
      mode: 'novo_terceiro',
      thirdParty: { name: name.trim(), phone, email: email.trim() || null },
    };
  }

  /** O CPF/CNPJ de quem recebe: obrigatório, conferido pelo tipo escolhido. */
  function validateDocument(): Errors {
    if (!ownDocumentRow) return {};
    const label = documentType === 'cpf' ? 'CPF' : 'CNPJ';
    if (document.replace(/\D/g, '') === '')
      return { document: `Informe o ${label} de quem recebe.` };
    const valid = documentType === 'cpf' ? isValidCpf(document) : isValidCnpj(document);
    return valid ? {} : { document: `${label} inválido: confira os dígitos.` };
  }

  function validateOwner(): Errors {
    if (editing) return {};
    const e: Errors = {};
    if (refType === '') {
      e.refType = 'Escolha quem recebe.';
      return e;
    }
    if (refType === 'associado') {
      if (!member) e.memberDocument = 'Procure o associado pelo CPF ou CNPJ.';
      return e;
    }
    if (mode === 'existente') {
      if (!ownerId) {
        e.owner = `Escolha ${
          refType === 'companhia'
            ? companyWords(companyType).the
            : refType === 'terceiro'
              ? 'o terceiro'
              : 'o consultor'
        }.`;
      }
      return e;
    }
    if (!name.trim()) e.name = 'Informe o nome.';
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      e.email = 'E-mail inválido.';
    }
    if (refType === 'companhia') {
      const valid = documentType === 'cpf' ? isValidCpf(document) : isValidCnpj(document);
      if (!valid) e.document = `${documentType === 'cpf' ? 'CPF' : 'CNPJ'} inválido.`;
      if (phone.trim() && !normalizePhone(phone))
        e.phone = 'Telefone inválido: DDD e número.';
    } else if (!normalizePhone(phone)) {
      e.phone = 'Informe o telefone com DDD.';
    }
    return e;
  }

  const submit = () => {
    const found: Errors = {
      ...validateOwner(),
      ...validateDocument(),
      ...(omitBankData
        ? {}
        : checkPaymentData({
            bankCode,
            agency,
            accountNumber,
            accountType,
            pixKeyType,
            pixKey,
          })),
    };
    setErrors(found);
    if (Object.keys(found).length === 0) save.mutate();
  };

  if (!allowed) {
    return (
      <Alert tone="alerta" title="Sem permissão">
        {editing
          ? 'Editar conta de pagamento exige a permissão de editar contas de pagamento.'
          : 'Cadastrar conta de pagamento exige a permissão de cadastrar contas de pagamento.'}{' '}
        Quem concede é a tela de grupos.
      </Alert>
    );
  }

  /*
   * Editar com agência e conta mascaradas gravaria `•••89-0` por cima da conta.
   * Quem abre a edição passa o beneficiário lido por `getBeneficiary`, inteiro;
   * se chegar o da lista, a tela recusa em vez de salvar a máscara.
   */
  if (editing && beneficiary.bankDataMasked) {
    return (
      <Alert tone="alerta" title="Dados bancários mascarados">
        Para editar, abra a conta de pagamento pela lista: a edição precisa da agência e
        da conta inteiras, e ver os dados bancários exige a permissão própria.
      </Alert>
    );
  }

  if (!editing && offeredTypes.length === 0) {
    return (
      <Alert tone="alerta" title="Nenhum tipo de conta disponível">
        Você pode cadastrar conta de pagamento, mas não procurar nem cadastrar nenhum dos
        donos que esta tela aceita. Peça a permissão de ver prestadores, instituições ou
        terceiros.
      </Alert>
    );
  }

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      noValidate
    >
      {failure && <Alert tone="erro">{failure}</Alert>}

      <FormSection title="Quem recebe">
        {editing ? (
          <p className="text-sm text-texto">
            <span className="font-medium">
              {beneficiary.owner.name ?? 'Nome indisponível no momento'}
            </span>
            <span className="text-texto-suave"> · {ownerKindLabel(beneficiary)}</span>
          </p>
        ) : (
          <>
            <FormRow cols={[12]}>
              <Select
                label="Tipo de conta"
                required
                value={kind}
                error={errors.refType}
                onChange={(e) => {
                  const next = e.target.value as OwnerKind | '';
                  setKind(next);
                  setWithoutBankData(false);
                  resetOwner(isCompanyType(next) ? 'cnpj' : 'cpf');
                }}
              >
                <option value="">Escolha…</option>
                {offeredPeople.map((t) => (
                  <option key={t} value={t}>
                    {REF_TYPE_LABELS[t]}
                  </option>
                ))}
                {offersCompany && (
                  <>
                    <optgroup label="Prestadores">
                      {COMPANY_TYPES_BY_CATEGORY.prestadores.map((t) => (
                        <option key={t} value={t}>
                          {COMPANY_TYPE_LABELS[t]}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Instituições">
                      {COMPANY_TYPES_BY_CATEGORY.instituicoes.map((t) => (
                        <option key={t} value={t}>
                          {COMPANY_TYPE_LABELS[t]}
                        </option>
                      ))}
                    </optgroup>
                  </>
                )}
              </Select>
            </FormRow>

            {refType === 'associado' && (
              <MemberLookup
                document={memberDocument}
                onDocumentChange={(next) => {
                  setMemberDocument(next);
                  setMember(null);
                }}
                member={member}
                onFound={(found) => {
                  setMember(found);
                  fillDocument(found.document);
                }}
                error={errors.memberDocument}
              />
            )}

            {refType === 'consultor' && (
              <ConsultantSearch
                value={ownerId}
                error={errors.owner}
                onChange={(id, label) => {
                  setOwnerId(id);
                  setOwnerLabel(label);
                }}
              />
            )}

            {(refType === 'companhia' || refType === 'terceiro') && (
              <>
                {canSearchOwner && canCreateOwner && (
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Dono">
                    <Button
                      type="button"
                      size="sm"
                      variant={mode === 'existente' ? 'secundario' : 'discreto'}
                      aria-pressed={mode === 'existente'}
                      onClick={() => {
                        setOwnerMode('existente');
                        setErrors({});
                      }}
                    >
                      Escolher cadastrado
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={mode === 'novo' ? 'secundario' : 'discreto'}
                      aria-pressed={mode === 'novo'}
                      onClick={() => {
                        setOwnerMode('novo');
                        resetOwner();
                      }}
                    >
                      Cadastrar{' '}
                      {refType === 'companhia'
                        ? companyWords(companyType).aNew
                        : 'novo terceiro'}
                    </Button>
                  </div>
                )}

                {mode === 'existente' ? (
                  <OwnerSearch
                    refType={refType}
                    companyType={companyType}
                    value={ownerId}
                    label={ownerLabel}
                    error={errors.owner}
                    onChange={(id, label, ownerDocument) => {
                      setOwnerId(id);
                      setOwnerLabel(label);
                      fillDocument(
                        ownerDocument,
                        refType === 'companhia' ? 'cnpj' : 'cpf',
                      );
                    }}
                  />
                ) : refType === 'companhia' ? (
                  <>
                    <FormRow cols={[12]}>
                      <Field
                        label="Nome"
                        required
                        maxLength={200}
                        value={name}
                        error={errors.name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </FormRow>
                    <FormRow cols={[4, 8]}>
                      <Select
                        label="Documento"
                        value={documentType}
                        onChange={(e) =>
                          setDocumentType(e.target.value as 'cpf' | 'cnpj')
                        }
                      >
                        <option value="cnpj">CNPJ</option>
                        <option value="cpf">CPF</option>
                      </Select>
                      <Field
                        label={documentType === 'cpf' ? 'CPF' : 'CNPJ'}
                        required
                        mask={documentType}
                        value={document}
                        error={errors.document}
                        onChange={(e) => setDocument(e.target.value)}
                      />
                    </FormRow>
                    <FormRow cols={[6, 6]}>
                      <Field
                        label="Telefone"
                        mask="phone"
                        value={phone}
                        error={errors.phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                      <Field
                        label="E-mail"
                        type="email"
                        value={email}
                        error={errors.email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </FormRow>
                  </>
                ) : (
                  <>
                    <FormRow cols={[12]}>
                      <Field
                        label="Nome do terceiro"
                        required
                        maxLength={200}
                        value={name}
                        error={errors.name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </FormRow>
                    <FormRow cols={[6, 6]}>
                      <Field
                        label="Telefone"
                        required
                        mask="phone"
                        value={phone}
                        error={errors.phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                      <Field
                        label="E-mail"
                        type="email"
                        value={email}
                        error={errors.email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </FormRow>
                  </>
                )}
              </>
            )}
          </>
        )}

        {ownDocumentRow && (
          <FormRow cols={[4, 8]}>
            <Select
              label="Documento"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value as 'cpf' | 'cnpj')}
            >
              <option value="cpf">CPF</option>
              <option value="cnpj">CNPJ</option>
            </Select>
            <Field
              label={`${documentType === 'cpf' ? 'CPF' : 'CNPJ'} de quem recebe`}
              required
              mask={documentType}
              value={document}
              error={errors.document}
              help={
                refType === 'terceiro' && !editing
                  ? 'O terceiro não tem documento no sinistro: informe o de quem vai receber.'
                  : refType === 'consultor' && !editing
                    ? 'O cadastro do consultor não tem documento: informe o de quem vai receber.'
                    : 'Vem do cadastro do dono quando ele tem; confira antes de salvar.'
              }
              onChange={(e) => setDocument(e.target.value)}
            />
          </FormRow>
        )}
      </FormSection>

      {canOmitBankData && (
        <FormSection
          title="Dados bancários"
          descricao="Instituição financeira e órgão público podem ficar sem conta e sem PIX quando o pagamento é por boleto ou guia."
        >
          <div className="flex flex-wrap gap-2" role="group" aria-label="Dados bancários">
            <Button
              type="button"
              size="sm"
              variant={withoutBankData ? 'discreto' : 'secundario'}
              aria-pressed={!withoutBankData}
              onClick={() => setWithoutBankData(false)}
            >
              Informar conta ou PIX
            </Button>
            <Button
              type="button"
              size="sm"
              variant={withoutBankData ? 'secundario' : 'discreto'}
              aria-pressed={withoutBankData}
              onClick={() => setWithoutBankData(true)}
            >
              Sem dados bancários
            </Button>
          </div>
          {withoutBankData && (
            <p className="text-sm text-texto-suave">
              Este cadastro fica sem conta e sem PIX. O boleto ou a guia vai anexado em
              cada despesa.
            </p>
          )}
        </FormSection>
      )}

      {!omitBankData && errors.payment && <Alert tone="erro">{errors.payment}</Alert>}

      {!omitBankData && (
        <>
          <FormSection
            title="Conta bancária"
            descricao="Banco, agência, conta e tipo vão juntos — ou nenhum deles, se o pagamento for por PIX."
          >
            <FormRow cols={[12]}>
              <SearchSelect
                label="Banco"
                placeholder="Escolha o banco…"
                searchPlaceholder="Buscar pelo nome ou pelo código…"
                emptyMessage="Nenhum banco com esse nome na lista do BACEN."
                options={BANK_OPTIONS}
                value={bankCode}
                onChange={setBankCode}
                error={errors.bankCode}
              />
            </FormRow>
            <FormRow cols={[4, 4, 4]}>
              <Field
                label="Agência"
                inputMode="numeric"
                maxLength={7}
                placeholder="0000"
                value={agency}
                error={errors.agency}
                onChange={(e) => setAgency(e.target.value)}
              />
              <Field
                label="Conta"
                inputMode="numeric"
                maxLength={16}
                placeholder="00000-0"
                value={accountNumber}
                error={errors.accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
              />
              <Select
                label="Tipo de conta"
                value={accountType}
                error={errors.accountType}
                onChange={(e) =>
                  setAccountType(e.target.value as BeneficiaryAccountType | '')
                }
              >
                <option value="">—</option>
                {BENEFICIARY_ACCOUNT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {ACCOUNT_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </FormRow>
            <FormRow cols={[12]}>
              <Field
                label="Titular da conta"
                maxLength={200}
                help="Só se for diferente de quem recebe."
                value={holder}
                onChange={(e) => setHolder(e.target.value)}
              />
            </FormRow>
          </FormSection>

          <FormSection title="PIX">
            <FormRow cols={[4, 8]}>
              <Select
                label="Tipo da chave"
                value={pixKeyType}
                error={errors.pixKeyType}
                onChange={(e) => setPixKeyType(e.target.value as PixKeyType | '')}
              >
                <option value="">—</option>
                {PIX_KEY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {PIX_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
              <Field
                label="Chave PIX"
                maxLength={120}
                mask={
                  pixKeyType === 'cpf' || pixKeyType === 'cnpj'
                    ? pixKeyType
                    : pixKeyType === 'telefone'
                      ? 'phone'
                      : undefined
                }
                inputMode={
                  pixKeyType === 'cpf' || pixKeyType === 'cnpj'
                    ? 'numeric'
                    : pixKeyType === 'telefone'
                      ? 'tel'
                      : pixKeyType === 'email'
                        ? 'email'
                        : 'text'
                }
                placeholder={
                  pixKeyType ? PIX_PLACEHOLDERS[pixKeyType] : 'Escolha o tipo primeiro'
                }
                disabled={pixKeyType === '' && pixKey === ''}
                value={pixKey}
                error={errors.pixKey}
                onChange={(e) => setPixKey(e.target.value)}
                onBlur={() => {
                  if (pixKeyType === '' || pixKey.trim() === '') return;
                  const check = checkPixKey(pixKeyType, pixKey);
                  setErrors((current) => ({
                    ...current,
                    pixKey: check.valid ? undefined : check.message,
                  }));
                }}
              />
            </FormRow>
          </FormSection>
        </>
      )}

      <FormFooter>
        <Button type="button" variant="discreto" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" loading={save.isPending}>
          {editing ? 'Salvar' : 'Cadastrar conta de pagamento'}
        </Button>
      </FormFooter>
    </Form>
  );
}

/**
 * A empresa ou o terceiro já cadastrado, procurado no servidor pelo nome ou
 * pelo documento. A lista muda a cada termo — por isso o `selectedLabel`, que
 * mantém o nome escolhido no gatilho quando ele sai da resposta.
 */
function OwnerSearch({
  refType,
  companyType,
  value,
  label,
  error,
  onChange,
}: {
  refType: 'companhia' | 'terceiro';
  companyType: Company['type'] | '';
  value: string;
  label: string;
  error?: string;
  /** O terceiro volta com documento `null`: ele não tem, e o operador digita. */
  onChange: (id: string, label: string, document: string | null) => void;
}) {
  const { data: context } = useSessionContext();
  const [term, setTerm] = useState('');
  const search = useDebouncedTerm(term.trim());
  const blocked = refType === 'companhia' && companyType === '';

  const query = useQuery({
    queryKey: ['sinistros', refType, context?.currentTenant.id, companyType, search],
    enabled: !blocked,
    queryFn: async () => {
      if (refType === 'companhia') {
        const r = await api.claims.listCompanies({
          query: {
            perPage: 20,
            type: companyType || undefined,
            search: search || undefined,
          },
        });
        return ok(r, 200, 'Não foi possível buscar os cadastros.').items.map((c) => ({
          value: String(c.id),
          label: `${c.name} · ${c.document}${c.archivedAt ? ' (arquivada)' : ''}`,
          document: c.document as string | null,
        }));
      }
      const r = await api.claims.listThirdParties({
        query: { perPage: 20, search: search || undefined },
      });
      return ok(r, 200, 'Não foi possível buscar os terceiros.').items.map((t) => ({
        value: String(t.id),
        label: `${t.name} · ${t.phone}${t.archivedAt ? ' (arquivado)' : ''}`,
        document: null as string | null,
      }));
    },
  });

  const words =
    refType === 'companhia'
      ? companyWords(companyType)
      : {
          label: 'Terceiro',
          the: 'o terceiro',
          aNew: 'novo terceiro',
          none: 'Nenhum terceiro',
        };
  return (
    <FormRow cols={[12]}>
      <SearchSelect
        label={words.label}
        required
        placeholder={blocked ? 'Escolha o tipo primeiro' : `Escolha ${words.the}…`}
        searchPlaceholder={
          refType === 'companhia'
            ? 'Buscar pelo nome ou CNPJ/CPF…'
            : 'Buscar pelo nome ou telefone…'
        }
        emptyMessage={
          search ? `${words.none} com "${search}".` : `${words.none} nesta base ainda.`
        }
        disabled={blocked}
        loading={query.isFetching}
        options={query.data ?? []}
        value={value}
        selectedLabel={label || undefined}
        onSearchChange={setTerm}
        onChange={(next) => {
          const option = query.data?.find((o) => o.value === next);
          onChange(next, option?.label ?? '', option?.document ?? null);
        }}
        error={query.isError ? query.error.message : error}
      />
    </FormRow>
  );
}

/**
 * O consultor da praça — `listBeneficiaryConsultants`, do próprio cadastro
 * (`platform.beneficiaries:criar`), que devolve a lista inteira (não pagina),
 * então a busca é local. Só os ativos: quem parou de vender continua dono das
 * vendas antigas, mas não é quem se cadastra para receber agora.
 */
function ConsultantSearch({
  value,
  error,
  onChange,
}: {
  value: string;
  error?: string;
  onChange: (id: string, label: string) => void;
}) {
  const { data: context } = useSessionContext();
  const query = useQuery({
    queryKey: [...BENEFICIARIES_KEY, 'consultores', context?.currentTenant.id],
    queryFn: async () => {
      const r = await api.beneficiaries.listBeneficiaryConsultants();
      return ok(r, 200, 'Não foi possível buscar os consultores.').map((c) => ({
        value: String(c.id),
        label: `${c.name} · voluntário ${c.erpVolunteerCode}`,
      }));
    },
  });

  return (
    <FormRow cols={[12]}>
      <SearchSelect
        label="Consultor"
        required
        placeholder="Escolha o consultor…"
        searchPlaceholder="Buscar pelo nome ou pelo código do voluntário…"
        emptyMessage="Nenhum consultor com esse nome nesta base."
        loading={query.isFetching}
        options={query.data ?? []}
        value={value}
        onChange={(next) => {
          const option = query.data?.find((o) => o.value === next);
          onChange(next, option?.label ?? '');
        }}
        error={query.isError ? query.error.message : error}
      />
    </FormRow>
  );
}

/**
 * O associado, achado no ERP pelo CPF ou CNPJ.
 *
 * A busca vai ao SGA (`POST /platform/beneficiaries/member-search`), e as
 * duas ausências voltam diferentes: "o ERP não tem" e "o ERP não respondeu".
 * A mensagem de cada uma é a do servidor, que já as distingue.
 */
function MemberLookup({
  document,
  onDocumentChange,
  member,
  onFound,
  error,
}: {
  document: string;
  onDocumentChange: (next: string) => void;
  member: BeneficiaryMember | null;
  onFound: (member: BeneficiaryMember) => void;
  error?: string;
}) {
  const find = useMutation({
    mutationFn: async () => {
      const r = await api.beneficiaries.findBeneficiaryMember({
        body: { document: document.replace(/\D/g, '') },
      });
      return ok(r, 200, 'Não foi possível procurar o associado.');
    },
    onSuccess: onFound,
  });
  const digits = document.replace(/\D/g, '');
  const complete = digits.length === 11 || digits.length === 14;

  return (
    <div className="flex flex-col gap-3">
      {/*
       * O botão vai no `trailing`, na linha do INPUT. Ao lado do `Field`, com
       * `items-end`, ele alinhava pelo fim do bloco — e o bloco termina na
       * ajuda ou no erro, que ficam embaixo do campo.
       */}
      <Field
        label="CPF ou CNPJ do associado"
        required
        mask="document"
        value={document}
        error={find.isError ? find.error.message : error}
        help={
          find.isError ? undefined : 'Procuramos no ERP, que é onde o associado está.'
        }
        onChange={(e) => onDocumentChange(e.target.value)}
        onKeyDown={(e) => {
          /* Enter procura, em vez de enviar o formulário inteiro. */
          if (e.key === 'Enter') {
            e.preventDefault();
            if (complete) find.mutate();
          }
        }}
        trailing={
          <Button
            type="button"
            variant="contorno"
            icon="busca"
            disabled={!complete}
            loading={find.isPending}
            onClick={() => find.mutate()}
            className="-ml-px shrink-0 rounded-l-none"
          >
            Procurar no ERP
          </Button>
        }
      />
      {member && (
        <Alert tone="sucesso" title={member.name ?? 'Associado sem nome no ERP'}>
          Código do associado {member.code}
          {member.document ? ` · documento ${member.document}` : ''}.
        </Alert>
      )}
    </div>
  );
}
